import React from "react";
import { DetailRow } from "tap/components/DetailRow";
import { usePPTState } from "tap/store/ppt/usePPT";
import { rightslineCatalogUrl } from "ppt/api";
import styles from "./styles.module.scss";

/**
 * Drawer body — the discount-as-offering view. Two sections:
 *
 *   1. Identity & Frame — what this offer IS, per PMX. Title / studio / format
 *      / territory plus source identifiers (PMX, Radar, Rightsline) so an
 *      operator can verify upstream linkage at a glance.
 *
 *   2. PMX Pricing Ladder — the rate-card rungs Disney offered for this
 *      discount, with cost / retail / post-TPR cost per rung. Not-offered
 *      rungs render greyed out so "did Disney offer Flexi B?" is answerable
 *      without leaving the drawer.
 *
 * Per-client realization (which clients took which rung, at which price, for
 * which window) lives in the inline master/detail panel and the Client
 * Discounts tab. We don't re-render it here.
 */

const formatCurrency = (value, currency = "USD") => {
  if (value == null) return "—";
  return value.toLocaleString(undefined, {
    style: "currency",
    currency,
  });
};

const RATE_CARDS = [
  {
    key: "wholesale",
    label: "Wholesale",
    costField: "tprWholesaleCost",
    retailField: "tprWholesaleRetail",
    postField: "postTprWholesaleCost",
  },
  {
    key: "revShare",
    label: "Rev Share",
    costField: "tprRevShareCost",
    retailField: "tprRevShareRetail",
    postField: "postTprRevShareCost",
  },
  {
    key: "flexiB",
    label: "Flexi B",
    costField: "tprFlexiBCost",
    retailField: "tprFlexiBRetail",
    postField: "postTprFlexiBCost",
  },
];

const Mono = ({ children }) =>
  children == null || children === "" ? (
    <span className={styles.empty}>—</span>
  ) : (
    <span className={styles.mono}>{children}</span>
  );

export const PPTDiscountDetails = () => {
  const { selectedDiscount } = usePPTState();
  if (!selectedDiscount) return null;
  const d = selectedDiscount;

  // Reverse-derive currency for the ladder display: prefer the rolled-up
  // currency, fall back to any child's currency, default to USD. The ladder
  // values are PMX scalars without currency metadata of their own.
  const ladderCurrency =
    d.currency || d.children?.find((c) => c.currency)?.currency || "USD";

  return (
    <>
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Identity</h4>
        <div className={styles.detailsGrid}>
          <DetailRow label="Title" value={d.title || "—"} />
          <DetailRow label="Studio" value={d.studio || "—"} />
          <DetailRow label="Territory" value={d.territory || "—"} />
          <DetailRow label="Format" value={d.format || "—"} />
          <DetailRow label="Catalog Type" value={d.contentClassification || "—"} />
          {d.media ? <DetailRow label="Media" value={d.media} /> : null}
          {d.tier ? <DetailRow label="Tier" value={d.tier} /> : null}
          {d.appleTier ? (
            <DetailRow label="Apple Tier" value={d.appleTier} />
          ) : null}
          {d.configuration ? (
            <DetailRow label="Configuration" value={d.configuration} />
          ) : null}
          {d.tprProgramName ? (
            <DetailRow label="Program Name" value={d.tprProgramName} />
          ) : null}
          <DetailRow
            label="Status"
            value={d.active ? "Active" : "Inactive"}
            noBorder
          />
        </div>
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Source Identifiers</h4>
        <div className={styles.detailsGrid}>
          <DetailRow
            label="PMX Discount ID"
            value={<Mono>{d.tprDiscountId}</Mono>}
          />
          <DetailRow
            label="Radar Product ID"
            value={<Mono>{d.radarProductId}</Mono>}
          />
          <DetailRow
            label="Rightsline Catalog ID"
            value={
              d.rightslineCatalogId ? (
                <a
                  href={rightslineCatalogUrl(d.rightslineCatalogId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Mono>{d.rightslineCatalogId}</Mono> ↗
                </a>
              ) : (
                <Mono>{d.rightslineCatalogId}</Mono>
              )
            }
            noBorder
          />
        </div>
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>PMX Pricing Ladder</h4>
        <table className={styles.ladderTable}>
          <thead>
            <tr>
              <th>Rate Card</th>
              <th className={styles.numCol}>Cost</th>
              <th className={styles.numCol}>Retail</th>
              <th className={styles.numCol}>Post-TPR Cost</th>
            </tr>
          </thead>
          <tbody>
            {RATE_CARDS.map((rc) => {
              const cost = d[rc.costField];
              const retail = d[rc.retailField];
              const postCost = d[rc.postField];
              const offered = cost != null || retail != null;
              return (
                <tr
                  key={rc.key}
                  className={offered ? "" : styles.notOffered}
                  title={offered ? undefined : "Not offered for this discount"}
                >
                  <td>{rc.label}</td>
                  <td className={styles.numCol}>
                    {offered ? formatCurrency(cost, ladderCurrency) : "—"}
                  </td>
                  <td className={styles.numCol}>
                    {offered ? formatCurrency(retail, ladderCurrency) : "—"}
                  </td>
                  <td className={styles.numCol}>
                    {postCost != null
                      ? formatCurrency(postCost, ladderCurrency)
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {ladderCurrency !== "USD" || d.currency ? null : (
          <p className={styles.ladderNote}>
            Pricing shown in {ladderCurrency} (default — children may have other
            currencies).
          </p>
        )}
      </section>
    </>
  );
};
